// AI scan pipeline — shared by Live Feed cells, Upload File, and Batch Scan.
// Splits one HF /analyze response into: enriched faces (with penduduk row),
// enriched plates (with kendaraan + owner row), vehicle objects, and other
// YOLO objects, then renders the full bbox spec onto a canvas overlay.

import { supabase } from './supabase';
import { lookupVehicle } from './vehicleLookup';
import { classifyGolongan, golonganBadge } from './vehicleGolongan';
import { getIntelLabel } from './statusLabel';

// Read current UI language dari localStorage agar bbox label di canvas
// menampilkan teks status sesuai bahasa aktif user (tanpa harus pass via props).
function currentLang() {
  try { return localStorage.getItem('horus.lang') || 'id'; } catch { return 'id'; }
}

import { cleanEnv } from './envClean';
const HF_URL = cleanEnv(import.meta.env.VITE_HF_SPACE_URL) || 'https://quantum2026ops-kes-cybercity.hf.space';
// Satu sumber kebenaran kelas kendaraan untuk seluruh aplikasi (NeoVision.jsx
// mengimpor ini; vehicleGolongan.js dan render bbox memakainya).
// 12 kelas D-FINE v2b (keputusan Andra #1050): car, truck, bus, motorcycle, van,
// angkot, becak, gerobak, sepeda, roda_tiga (license_plate bukan kendaraan,
// ditangani jalur plat). 'bicycle' ikut karena detektor lokal COCO-SSD memakai
// nama itu (setara 'sepeda' dari server).
export const VEHICLE_LABELS = new Set([
  'car', 'truck', 'motorcycle', 'bus', 'van',
  'angkot', 'becak', 'gerobak', 'sepeda', 'roda_tiga',
  'bicycle',
]);
// Scope deteksi: HANYA kendaraan bermotor, orang (via wajah), wajah, dan senjata.
// Label senjata bergantung model AI engine; di sini disiapkan supaya begitu engine
// mengembalikan kelas senjata, langsung tampil sebagai alert merah.
export const WEAPON_LABELS = new Set([
  'gun', 'pistol', 'rifle', 'handgun', 'firearm', 'shotgun',
  'knife', 'weapon', 'machete', 'sword',
]);

// Ping HF Space — triggers wake-up if sleeping. Resolves true if online.
export async function pingHFSpace(timeoutMs = 25000) {
  try {
    const res = await fetch(`/api/hf-health`, { signal: AbortSignal.timeout(timeoutMs) });
    return res.ok;
  } catch { return false; }
}
// FACE_MATCH_THRESHOLD = 0.38 — untuk live camera scan (NeoVision) client-side
// embeddings ref match. Domain berbeda dari OSINT face compare (cross-domain
// KTP vs sosmed yang pakai OSINT_THRESHOLDS di src/lib/osint/thresholds.js).
// Live scan dapat threshold lebih ketat karena foto same-camera same-lighting.
export const FACE_MATCH_THRESHOLD = 0.38;

// Alert dedup TTL — exposed as constants supaya konsisten dengan dispatchAlerts
// dan dapat dipakai oleh modul lain (UI badge, test, dsb).
export const ALERT_DEDUP_KNOWN   = 60_000;   // 60 detik untuk wajah dikenal
export const ALERT_DEDUP_UNKNOWN = 300_000;  // 5 menit untuk wajah tidak dikenal
export const ALERT_DEDUP_VEHICLE = 60_000;   // 60 detik untuk kendaraan

// ── Plate normalization (F0-A8 — robust) ────────────────────────────────────
// Format Indonesia: 1-2 huruf wilayah + 1-4 angka + 1-3 huruf seri (B 1234 XY).
// OCR sering misread karakter look-alike — fungsi ini koreksi posisi-aware:
//   Letter slot (prefix/suffix): 0→O, 1→I, 2→Z, 5→S, 6→G, 8→B
//   Number slot (middle):        O,Q,D→0, I,L→1, Z→2, S→5, G→6, B→8, T→7
// Validasi prefix terhadap daftar kode wilayah resmi RI.

// Daftar kode wilayah valid (1-2 huruf, sebagian besar berdasar TNKB Polri 2024)
const REGION_CODES = new Set([
  // 1-huruf
  'A','B','D','E','F','G','H','K','L','M','N','P','R','S','T','W','Z',
  // 2-huruf — Jawa
  'AA','AB','AD','AE','AG','BA','BB','BD','BE','BG','BH','BK','BL','BM','BN','BP',
  // 2-huruf — Sumatera
  'BA','BB','BD','BE','BG','BH','BK','BL','BM','BN','BP',
  // 2-huruf — Kalimantan + Sulawesi + Bali + Nusa Tenggara
  'DA','DB','DC','DD','DE','DG','DH','DK','DL','DM','DN','DR','DS','DT','EA','EB',
  // 2-huruf — Papua + Maluku
  'KB','KH','KT','KU','PA','PB',
  // Khusus: RFB (Polri), RFD/RFP/RFS (TNI/Polri level)
  'RFB','RFD','RFP','RFS','RI',
]);

const FIX_LETTER = { '0':'O', '1':'I', '2':'Z', '5':'S', '6':'G', '8':'B' };
const FIX_NUMBER = { 'O':'0', 'Q':'0', 'D':'0', 'I':'1', 'L':'1', 'Z':'2', 'S':'5', 'G':'6', 'B':'8', 'T':'7' };

function fixToLetter(s) { return s.split('').map(c => FIX_LETTER[c] || c).join(''); }
function fixToNumber(s) { return s.split('').map(c => FIX_NUMBER[c] || c).join(''); }

export function normalizePlat(raw) {
  if (!raw) return '';
  // Bersihkan: uppercase, drop non-alphanumeric, normalize whitespace
  let clean = String(raw).toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim().replace(/\s+/g, ' ');
  const noSpace = clean.replace(/\s/g, '');
  if (!noSpace) return '';

  // Path 1 — fast match standard format
  let m = noSpace.match(/^([A-Z]{1,2})(\d{1,4})([A-Z]{1,3})$/);
  if (m && (REGION_CODES.has(m[1]) || m[1].length === 2)) {
    return `${m[1]} ${m[2]} ${m[3]}`;
  }

  // Path 2 — try OCR-corrected with all valid prefix/middle/suffix splits
  const len = noSpace.length;
  if (len < 4 || len > 9) return clean; // out of range plate length

  let bestMatch = null;
  let bestScore = -1;
  for (let prefixLen = 1; prefixLen <= 2; prefixLen++) {
    for (let suffixLen = 1; suffixLen <= 3; suffixLen++) {
      const middleLen = len - prefixLen - suffixLen;
      if (middleLen < 1 || middleLen > 4) continue;
      const rawPrefix = noSpace.slice(0, prefixLen);
      const rawMiddle = noSpace.slice(prefixLen, prefixLen + middleLen);
      const rawSuffix = noSpace.slice(prefixLen + middleLen);
      const prefix = fixToLetter(rawPrefix);
      const middle = fixToNumber(rawMiddle);
      const suffix = fixToLetter(rawSuffix);
      if (!/^[A-Z]+$/.test(prefix) || !/^\d+$/.test(middle) || !/^[A-Z]+$/.test(suffix)) continue;

      // Score: prefer (a) valid region code, (b) fewer character substitutions
      const subCount =
        (rawPrefix === prefix ? 0 : 1) +
        (rawMiddle === middle ? 0 : 1) +
        (rawSuffix === suffix ? 0 : 1);
      const regionBonus = REGION_CODES.has(prefix) ? 10 : 0;
      const score = regionBonus - subCount;
      if (score > bestScore) {
        bestScore = score;
        bestMatch = `${prefix} ${middle} ${suffix}`;
      }
    }
  }
  return bestMatch || clean;
}

// Multi-frame consensus — pick plate teks paling sering muncul dari N frame.
// Dipakai di scan loop untuk vote across consecutive scans (lebih akurat).
export function platConsensus(candidates) {
  if (!Array.isArray(candidates) || candidates.length === 0) return null;
  const tally = new Map();
  for (const c of candidates) {
    const norm = normalizePlat(c);
    if (!norm) continue;
    tally.set(norm, (tally.get(norm) || 0) + 1);
  }
  if (!tally.size) return null;
  // Sort by frequency, return highest
  const sorted = [...tally.entries()].sort((a, b) => b[1] - a[1]);
  return sorted[0][0];
}

// ── Tier-2 vision OCR fallback (Qwen2.5-VL via Vercel proxy) ─────────────────
// Dipanggil jika confidence OCR Tier-1 di bawah 0.75. Memanggil server-side
// proxy `/api/confirm-plate` (di Vercel) — token HF disimpan di server env
// (HF_TOKEN, tanpa VITE_ prefix) sehingga TIDAK ke-bundle ke client.
// Mengembalikan plat ter-normalize, atau null bila tidak yakin.
export async function confirmPlateWithVision(cropBlob, rawText) {
  if (!cropBlob) return null;
  try {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(cropBlob);
    });
    const r = await fetch('/api/confirm-plate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: base64, rawText: String(rawText || '').slice(0, 32) }),
      signal: AbortSignal.timeout(13000),
    });
    if (!r.ok) {
      console.warn('[confirmPlateWithVision] proxy HTTP', r.status);
      return null;
    }
    const { plate } = await r.json();
    return plate ? (normalizePlat(plate) || null) : null;
  } catch (e) {
    console.warn('[confirmPlateWithVision] fail:', e?.message || e);
    return null;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function maskNik(nik) {
  if (!nik || typeof nik !== 'string') return '—';
  if (nik.length < 8) return nik;
  return nik.slice(0, 4) + '*'.repeat(Math.max(0, nik.length - 8)) + nik.slice(-4);
}

export function calcAge(tanggal_lahir) {
  if (!tanggal_lahir) return null;
  const birth = new Date(tanggal_lahir);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() ||
     (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

function cosineSim(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return 0;
  let dot = 0, na = 0, nb = 0;
  for (let i = 0; i < a.length; i++) { dot += a[i]*b[i]; na += a[i]*a[i]; nb += b[i]*b[i]; }
  if (na === 0 || nb === 0) return 0;
  return dot / Math.sqrt(na * nb);
}

// Margin minimal antara kandidat-1 dan kandidat-2. Kalau dua orang berbeda
// sama-sama dekat, ID dianggap ambigu → JANGAN klaim kenal (cegah salah-kenal).
const FACE_MATCH_MARGIN = 0.05;

// threshold opsional — pass nilai lebih rendah ketika embedding sudah dirata-rata
// dari beberapa frame (multi-frame vote buffer), karena vektor rata-rata lebih stabil.
function clientMatch(embedding, knownFaces, threshold = FACE_MATCH_THRESHOLD) {
  if (!Array.isArray(embedding) || !embedding.length || !knownFaces?.length) return null;
  let best = { nik: null, sim: -1 };
  let second = { nik: null, sim: -1 };
  for (const kf of knownFaces) {
    if (!Array.isArray(kf.emb)) continue;
    const sim = cosineSim(embedding, kf.emb);
    if (sim > best.sim) { second = best; best = { nik: kf.nik, sim }; }
    else if (sim > second.sim) { second = { nik: kf.nik, sim }; }
  }
  if (best.sim < threshold) return null;
  // Ambigu: kandidat lain (orang berbeda) hampir sama dekatnya → tolak.
  if (second.nik && second.nik !== best.nik
      && second.sim >= threshold
      && (best.sim - second.sim) < FACE_MATCH_MARGIN) {
    return null;
  }
  return { nik: best.nik, sim: best.sim };
}

// Supabase server-side cosine match — fallback ketika client cosine tidak menemukan hasil.
// Memanggil SQL function match_face(query_emb jsonb, threshold float, match_count int).
//
// RPC mungkin belum di-deploy di production Supabase (404 → spam every face detect).
// Cache failure di module flag → skip future calls in session jika 404.
let _matchFaceRpcDisabled = false;
async function supabaseMatchFace(embedding, threshold = FACE_MATCH_THRESHOLD) {
  if (_matchFaceRpcDisabled) return null;
  if (!Array.isArray(embedding) || !embedding.length) return null;
  try {
    const { data, error } = await supabase.rpc('match_face', {
      query_emb:   embedding,
      threshold,
      match_count: 2,  // ambil 2 utk cek margin ambiguitas (cegah salah-kenal)
    });
    if (error) {
      // RPC tidak ada / signature mismatch — disable session-wide, log sekali.
      // 404, PGRST202, 42883 (function does not exist), atau message "not found".
      const msg = String(error.message || '');
      const code = String(error.code || '');
      if (code === 'PGRST202' || code === '42883' || /not found|does not exist|404/i.test(msg)) {
        _matchFaceRpcDisabled = true;
        console.warn('[supabaseMatchFace] RPC match_face tidak tersedia — skip future calls. Run migration 2026-05-01_match_face_fn.sql di Supabase SQL Editor.');
      }
      return null;
    }
    if (!data?.length) return null;
    // Cek margin ambiguitas: kalau kandidat-2 (orang beda) terlalu dekat, tolak.
    if (data.length >= 2 && data[1].nik !== data[0].nik
        && data[1].sim >= threshold
        && (data[0].sim - data[1].sim) < FACE_MATCH_MARGIN) {
      return null;
    }
    return { nik: data[0].nik, sim: data[0].sim };
  } catch (err) {
    // Network errors etc — disable to avoid spam if persistent
    if (/404|not found/i.test(String(err?.message || ''))) {
      _matchFaceRpcDisabled = true;
    }
    return null;
  }
}

// In-memory penduduk cache shared across all callers — full KTP row
const _pendudukCache = new Map();
export async function lookupPenduduk(nik) {
  if (!nik) return null;
  if (_pendudukCache.has(nik)) return _pendudukCache.get(nik);
  const { data } = await supabase.from('penduduk')
    .select(`nik, nama_lengkap, tempat_lahir, tanggal_lahir, jenis_kelamin,
             gol_darah, agama, status_perkawinan, pekerjaan, kewarganegaraan,
             alamat_lengkap, rt_rw, kel_desa, kecamatan, kota_kabupaten,
             status_intelijen, catatan_fisik, foto_url,
             last_seen, lokasi_last, confidence_last`)
    .eq('nik', nik).maybeSingle();
  _pendudukCache.set(nik, data || null);
  return data || null;
}

// Plate list for a given NIK (kendaraan owned by that person)
const _vehiclesByNikCache = new Map();
export async function lookupVehiclesForNik(nik) {
  if (!nik) return [];
  if (_vehiclesByNikCache.has(nik)) return _vehiclesByNikCache.get(nik);
  const { data } = await supabase.from('kendaraan')
    .select('no_plat, merk_model, warna, tipe_kendaraan, masa_berlaku_stnk')
    .eq('nik_pemilik', nik);
  const list = data || [];
  _vehiclesByNikCache.set(nik, list);
  return list;
}

export function clearPendudukCache() { _pendudukCache.clear(); _vehiclesByNikCache.clear(); }

// Supabase-based penduduk search — backup ketika HF Space offline.
// Searches by nama_lengkap (ilike) OR nik prefix — no HF required.
export async function searchPenduduk(query, limit = 10) {
  const q = (query || '').trim();
  if (q.length < 2) return [];
  try {
    const { data } = await supabase.from('penduduk')
      .select('nik, nama_lengkap, status_intelijen, foto_url, kota_kabupaten, jenis_kelamin, tanggal_lahir')
      .or(`nama_lengkap.ilike.%${q}%,nik.ilike.%${q}%`)
      .order('nama_lengkap')
      .limit(limit);
    return data || [];
  } catch { return []; }
}

// ── Scan a frame: POST → enrich faces + plates ───────────────────────────────
// Pakai Vercel serverless proxy `/api/analyze` (server-side menambah auth ke
// HF backend yang private). Frontend tidak boleh punya HF_TOKEN.

// Vercel platform body limit ~4.5MB. Downscale image/video frame to < 3.5MB
// JPEG sebelum upload — fixes 413 ketika user upload foto/video besar.
const MAX_UPLOAD_BYTES = 3_500_000;
const MAX_LONG_SIDE = 1920;

async function blobToBitmap(input) {
  if (input instanceof File && input.type.startsWith('video/')) {
    // Extract first frame from video
    const url = URL.createObjectURL(input);
    try {
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.crossOrigin = 'anonymous';
      v.src = url;
      await new Promise((res, rej) => {
        // Fallback: kalau seek tak pernah selesai (durasi tak diketahui di
        // sebagian container MKV/MOV), pakai frame pertama yang sudah ter-decode
        // saat loadeddata daripada menggantung sampai abort 60s.
        const t = setTimeout(res, 3000);
        v.onloadeddata = () => {
          const target = Number.isFinite(v.duration) ? Math.min(0.1, v.duration / 4) : 0.1;
          v.currentTime = target;
        };
        v.onseeked = () => { clearTimeout(t); res(); };
        v.onerror = (e) => { clearTimeout(t); rej(e); };
      });
      return await createImageBitmap(v);
    } finally { URL.revokeObjectURL(url); }
  }
  return await createImageBitmap(input);
}

async function downscaleIfNeeded(input) {
  // Already small + plain blob → use as-is
  if (input instanceof Blob && !(input instanceof File) && input.size <= MAX_UPLOAD_BYTES) {
    return input;
  }
  // For images & videos: always normalize to JPEG ≤ 1920px on long side
  try {
    const bmp = await blobToBitmap(input);
    let w = bmp.width, h = bmp.height;
    if (Math.max(w, h) > MAX_LONG_SIDE) {
      const scale = MAX_LONG_SIDE / Math.max(w, h);
      w = Math.round(w * scale); h = Math.round(h * scale);
    }
    const canvas = (typeof OffscreenCanvas !== 'undefined')
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
    canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    let q = 0.85;
    let out = await (canvas.convertToBlob
      ? canvas.convertToBlob({ type: 'image/jpeg', quality: q })
      : new Promise(res => canvas.toBlob(res, 'image/jpeg', q)));
    // Re-encode at lower quality if still too large
    while (out.size > MAX_UPLOAD_BYTES && q > 0.4) {
      q -= 0.15;
      out = await (canvas.convertToBlob
        ? canvas.convertToBlob({ type: 'image/jpeg', quality: q })
        : new Promise(res => canvas.toBlob(res, 'image/jpeg', q)));
    }
    return out;
  } catch (e) {
    console.warn('[callHFAnalyze] downscale failed, sending raw:', e?.message);
    return input;
  }
}

// Default 60s — first request cold-loads YOLO+ArcFace+OCR (~27s). Setelah warm,
// calls sub-second jadi timeout besar tidak hurt steady-state, tapi cover
// cold-start.
export async function callHFAnalyze(blob, timeoutMs = 60000, extraFields = null) {
  const payload = await downscaleIfNeeded(blob);
  const fd = new FormData();
  fd.append('file', payload, 'frame.jpg');
  // known_vehicles: JSON [[x1,y1,x2,y2],...] kendaraan yang platnya sudah terbaca; server melewati OCR untuknya.
  if (extraFields) for (const [k, v] of Object.entries(extraFields)) fd.append(k, v);
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch('/api/analyze', { method: 'POST', body: fd, signal: ctrl.signal });
    if (!res.ok) throw new Error(`AI ${res.status}`);
    return await res.json();
  } finally { clearTimeout(timer); }
}

export async function enrichScanResult(rawData, embeddingsRef, sourceImage = null) {
  const faces       = rawData.faces || [];
  const objectsAll  = rawData.objects || [];
  const vehicleObjs = objectsAll.filter(o => VEHICLE_LABELS.has(o.label));
  // Warna (piksel, lokal) + golongan Bina Marga/PUPR (dari kelas YOLO + ukuran).
  const frameH = sourceImage ? (sourceImage.naturalHeight || sourceImage.videoHeight || sourceImage.height || 0) : 0;
  for (const o of vehicleObjs) {
    o._warna = sourceImage ? detectVehicleColor(sourceImage, o.bbox) : null;
    o._golongan = classifyGolongan(o, frameH);
  }
  // Senjata = kategori prioritas tinggi (alert merah). Label tergantung model.
  const weaponObjs  = objectsAll.filter(o => WEAPON_LABELS.has((o.label || '').toLowerCase()));
  // HORUS scope: HANYA orang (via face detection) + kendaraan bermotor + senjata.
  // Drop SEMUA objek lain (chair, table, dll) — orang sudah di-handle face detection.
  const otherObjs   = [];
  const plates      = rawData.plates || [];

  // Faces — matching chain (priority order):
  //   1. Client-side cosine  (fast, uses embeddingsRef loaded from Supabase at boot)
  //   2. Supabase pgvector   (server-side cosine, fallback jika client miss)
  //   3. HF match_nik        (HF server-side result, last resort)
  //
  // Adaptive threshold: embedding yang sudah dirata-rata dari >=3 frame
  // (multi-frame vote buffer) lebih stabil, sehingga threshold sedikit
  // lebih rendah aman dipakai (0.35 vs default 0.38).
  const VOTE_THRESHOLD_BOOST = 0.03; // turunkan threshold saat embedding sudah stabil
  const enrichedFaces = [];
  for (const f of faces) {
    let nik = null, score = f.match_score || 0;
    const voteFrames = f._voteFrames || 1;
    const adaptThreshold = voteFrames >= 3
      ? FACE_MATCH_THRESHOLD - VOTE_THRESHOLD_BOOST
      : FACE_MATCH_THRESHOLD;
    const cm = clientMatch(f.embedding, embeddingsRef?.current || [], adaptThreshold);
    if (cm) {
      nik = cm.nik; score = cm.sim;
    } else if (f.embedding?.length) {
      // Client cosine miss — try Supabase pgvector as server-side fallback
      const sm = await supabaseMatchFace(f.embedding, adaptThreshold);
      if (sm) { nik = sm.nik; score = sm.sim; }
      else if (f.status === 'KNOWN' && f.match_nik) { nik = f.match_nik; }
    } else if (f.status === 'KNOWN' && f.match_nik) { nik = f.match_nik; }
    const person   = nik ? await lookupPenduduk(nik) : null;
    const vehicles = nik ? await lookupVehiclesForNik(nik) : [];
    enrichedFaces.push({ ...f, _person: person, _vehicles: vehicles, match_score: score, match_nik: nik });
  }

  // Plates — pipeline 3-tier:
  //   Tier-1: TrOCR Indonesia di backend (ziyadazz/OCR-PLAT-NOMOR-INDONESIA, native)
  //   Tier-2: EasyOCR fallback di backend (jika TrOCR low-conf)
  //   Tier-3: Qwen2.5-VL via /api/confirm-plate (kalau backend OCR gagal/low-conf
  //           AND DB close-match miss). Tier-3 adalah paid call → hemat di sini.
  // Backend response punya field `source` ('trocr' | 'easyocr') + `confidence`.
  // Kita validasi via DB close-match dulu (Levenshtein ≤2 bisa koreksi 8↔B, 0↔O),
  // lalu hanya jatuh ke Qwen Tier-3 kalau benar-benar tidak yakin.
  const enrichedPlates = [];
  for (const p of plates) {
    const rawText = p.text || '';
    const backendSource = p.source || 'backend';
    let plate = normalizePlat(rawText);
    let confidence = p.confidence ?? 0.5;
    let confirmed = false;
    let source = backendSource;

    // Step 1 — DB close-match dulu (auto-correct typical OCR confusion)
    let info = null;
    try { info = await lookupVehicle(plate); } catch { /* ignore */ }
    if (info) {
      if (info.no_plat && info.no_plat !== plate) {
        console.log(`[plate] auto-correct: '${plate}' → '${info.no_plat}' (DB)`);
        plate = info.no_plat;
      }
      confidence = Math.max(confidence, 0.95);
      confirmed = true;
    }

    // Step 2 — Qwen Tier-3 fallback kalau:
    //   (a) DB miss DAN backend confidence rendah (< 0.65), ATAU
    //   (b) backend pakai EasyOCR (Tier-2 backend, tidak akurat untuk plat ID), ATAU
    //   (c) hasil normalize tidak match format plat ID (length < 4 atau bukan strict)
    const looksValid = /^[A-Z]{1,2}\s\d{1,4}\s[A-Z]{1,3}$/.test(plate);
    const needTier3 = !confirmed && (
      (confidence < 0.65) ||
      (backendSource === 'easyocr') ||
      (!looksValid && plate.length < 4)
    );
    if (needTier3 && sourceImage && p.from_bbox) {
      try {
        const cropBlob = await cropBboxToBlob(sourceImage, p.from_bbox);
        if (cropBlob) {
          const visionResult = await confirmPlateWithVision(cropBlob, rawText);
          if (visionResult) {
            const normalized = normalizePlat(visionResult);
            if (normalized && normalized.length >= 4) {
              // Re-validasi via DB
              try {
                const info2 = await lookupVehicle(normalized);
                if (info2) {
                  plate = info2.no_plat || normalized;
                  info = info2;
                  confidence = 0.95;
                  confirmed = true;
                  source = 'qwen+db';
                } else {
                  plate = normalized;
                  confidence = 0.92;
                  source = 'qwen';
                }
              } catch {
                plate = normalized;
                confidence = 0.92;
                source = 'qwen';
              }
            }
          }
        }
      } catch (e) { /* keep backend result */ }
    }

    enrichedPlates.push({
      plate, from_bbox: p.from_bbox, info,
      confidence, confirmed, raw_text: rawText, source,
    });
  }

  // ── Dedupe ALL plates (confirmed + unregistered): kalau plate string sama
  // muncul di > 1 bbox, keep bbox dengan confidence tertinggi.
  // Berlaku untuk SEMUA case:
  //   1. Confirmed: 2 mobil dapat plate DB "B 1522 QGK" → keep best conf
  //   2. Unregistered: 2 mobil dapat OCR text "ABC123" → keep best conf
  //   3. Empty plate (no OCR): tidak di-dedupe (each bbox punya from_bbox unik)
  const dedupedPlates = (() => {
    const byPlate = new Map();    // plate string → best entry
    const noPlate = [];           // bboxes tanpa plate text — tidak di-dedupe
    for (const e of enrichedPlates) {
      const key = (e.plate || '').trim().toUpperCase();
      if (!key) { noPlate.push(e); continue; }
      const existing = byPlate.get(key);
      if (!existing || (e.confidence || 0) > (existing.confidence || 0)) {
        byPlate.set(key, e);
      }
    }
    const result = [...byPlate.values(), ...noPlate];
    if (enrichedPlates.length !== result.length) {
      console.log(`[plate-dedupe] ${enrichedPlates.length} → ${result.length} (removed duplicate plate strings)`);
    }
    return result;
  })();

  return { enrichedFaces, enrichedPlates: dedupedPlates, vehicleObjs, otherObjs, weaponObjs };
}

// Helper: crop bbox dari sourceImage (HTMLImageElement/Canvas/Video) → JPEG blob
// Untuk Tier-2 vision OCR yang butuh image crop sebagai input.
async function cropBboxToBlob(sourceImage, bbox) {
  if (!sourceImage || !bbox) return null;
  const [x1, y1, x2, y2] = bbox;
  const w = Math.max(0, x2 - x1);
  const h = Math.max(0, y2 - y1);
  if (w < 8 || h < 8) return null; // terlalu kecil untuk OCR
  // Padding 10% untuk konteks (kadang OCR butuh lihat border)
  const pad = Math.round(Math.min(w, h) * 0.1);
  const px1 = Math.max(0, x1 - pad);
  const py1 = Math.max(0, y1 - pad);
  const pw = w + pad * 2;
  const ph = h + pad * 2;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = pw;
    canvas.height = ph;
    const ctx = canvas.getContext('2d');
    // Upscale 2x untuk small plates (better OCR)
    if (pw < 200 && typeof OffscreenCanvas !== 'undefined') {
      canvas.width = pw * 2;
      canvas.height = ph * 2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(sourceImage, px1, py1, pw, ph, 0, 0, pw * 2, ph * 2);
    } else {
      ctx.drawImage(sourceImage, px1, py1, pw, ph, 0, 0, pw, ph);
    }
    return await new Promise(r => canvas.toBlob(b => r(b), 'image/jpeg', 0.92));
  } catch (e) {
    console.warn('[cropBboxToBlob]', e?.message);
    return null;
  }
}

// ── Deteksi warna kendaraan (lokal, tanpa server) ────────────────────────────
// Sampling area bodi kendaraan (hindari kaca depan/atap & aspal bawah), rata-rata
// RGB → klasifikasi ke nama warna Indonesia via HSV. Gratis & real-time.
function rgbToColorNameID(r, g, b) {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const v = max / 255;
  const s = max === 0 ? 0 : (max - min) / max;
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  // Achromatic (hitam/putih/abu) — saturasi rendah
  if (v < 0.20) return 'Hitam';
  if (s < 0.16) return v > 0.72 ? 'Putih' : v > 0.42 ? 'Silver' : 'Abu-abu';
  // Berwarna — berdasar hue
  if (h < 15 || h >= 345) return 'Merah';
  if (h < 45)  return 'Oranye';
  if (h < 68)  return 'Kuning';
  if (h < 168) return 'Hijau';
  if (h < 200) return 'Toska';
  if (h < 255) return 'Biru';
  if (h < 290) return 'Ungu';
  return 'Merah Muda';
}

function detectVehicleColor(sourceImage, bbox) {
  try {
    if (!sourceImage || !bbox) return null;
    const [x1, y1, x2, y2] = bbox;
    const w = x2 - x1, h = y2 - y1;
    if (w < 16 || h < 16) return null;
    // Region bodi: horizontal tengah 60%, vertikal 25%–65% (badan/kap, bukan
    // kaca-atap atau ban-aspal). Cukup andal untuk mobil & motor.
    const sx = x1 + w * 0.20, sy = y1 + h * 0.25;
    const sw = w * 0.60, sh = h * 0.40;
    const S = 24;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(sourceImage, sx, sy, sw, sh, 0, 0, S, S);
    const { data } = ctx.getImageData(0, 0, S, S); // throws jika canvas tainted → catch
    let r = 0, g = 0, b = 0, n = 0;
    for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2]; n++; }
    if (!n) return null;
    r /= n; g /= n; b /= n;
    return { name: rgbToColorNameID(r, g, b), rgb: [Math.round(r), Math.round(g), Math.round(b)] };
  } catch { return null; }
}

// ── Drawing primitives ───────────────────────────────────────────────────────

export function drawCorners(ctx, x1, y1, x2, y2, color, lineW) {
  const cl = Math.min(16, (x2 - x1) * 0.2);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineW;
  ctx.beginPath();
  [[x1,y1+cl,x1,y1,x1+cl,y1],[x2-cl,y1,x2,y1,x2,y1+cl],
   [x2,y2-cl,x2,y2,x2-cl,y2],[x1+cl,y2,x1,y2,x1,y2-cl]]
    .forEach(([ax,ay,bx,by,cx,cy]) => { ctx.moveTo(ax,ay); ctx.lineTo(bx,by); ctx.lineTo(cx,cy); });
  ctx.stroke();
}

export function drawInfoCard(ctx, x, y, lines, opt) {
  const padX = 8, padY = 6, lineH = opt.lineH || 14;
  const widths = lines.map(l => {
    ctx.font = l.bold ? `bold ${l.size || 12}px "IBM Plex Mono", monospace` : `${l.size || 11}px "IBM Plex Mono", monospace`;
    return ctx.measureText(l.text).width;
  });
  const cardW = Math.max(...widths, opt.minW || 0) + padX * 2;
  const cardH = lines.length * lineH + padY * 2 + 2;
  ctx.fillStyle = opt.bg;
  ctx.beginPath(); ctx.roundRect(x, y, cardW, cardH, 4); ctx.fill();
  if (opt.accent) {
    ctx.fillStyle = opt.accent;
    ctx.beginPath(); ctx.roundRect(x, y, cardW, 2, [4,4,0,0]); ctx.fill();
  }
  let cy = y + padY + lineH - 3;
  for (const l of lines) {
    ctx.font = l.bold ? `bold ${l.size || 12}px "IBM Plex Mono", monospace` : `${l.size || 11}px "IBM Plex Mono", monospace`;
    ctx.fillStyle = l.color || opt.fg;
    ctx.fillText(l.text, x + padX, cy);
    cy += lineH;
  }
}

// ── High-level bbox per category ─────────────────────────────────────────────

// Sprint 004 F-001 — State colors:
//   identifying (pre-enrich): #ffd60a yellow + "Memindai..."
//   identified+CLEAR:         #00ff88 green
//   unregistered / WANTED/SUSPECT: #ff2d55 red
export const STATE_COLOR = {
  identifying: '#ffd60a',
  identified:  '#00ff88',
  unregistered: '#ff2d55',
  wanted:      '#ff2d55',
};

export function drawFaceBbox(ctx, face, w, h) {
  const [x1, y1, x2, y2] = face.bbox;
  const intel  = face._person?.status_intelijen || null;
  const isKnown = !!face._person;
  // Wajah dikenali (Space/klien) tetapi baris penduduknya tidak ada: jangan dilabeli "TIDAK DIKENAL".
  const isKnownNoRow = !isKnown && !!face.match_nik;
  const isWantedOrSuspect = intel === 'WANTED' || intel === 'SUSPECT';
  const isWanted = intel === 'WANTED';

  // F-001 state colors: green = identified+CLEAR, red = unregistered/wanted/suspect.
  let boxColor, cardBg, cardFg, accent;
  if (isKnown && !isWantedOrSuspect) {
    boxColor = STATE_COLOR.identified;
    cardBg = 'rgba(0,255,136,0.92)'; cardFg = '#02060e'; accent = '#00d4ff';
  } else {
    boxColor = STATE_COLOR.unregistered;
    cardBg = 'rgba(255,45,85,0.92)'; cardFg = '#ffffff'; accent = '#ff2d55';
  }

  // Kotak wajah: sudut saja tanpa isian (permintaan Andra 19 Okt)
  drawCorners(ctx, x1, y1, x2, y2, boxColor, isWanted ? 3 : 2.2);

  const conf = Math.round((face.match_score ?? face.confidence ?? 0) * 100);
  const plates = (face._vehicles || []).map(v => v.no_plat).filter(Boolean);
  const lines = [];
  if (isKnown) {
    const p = face._person;
    const umur = p.tanggal_lahir ? calcAge(p.tanggal_lahir) : null;
    const gShort = p.jenis_kelamin === 'LAKI-LAKI' ? 'L' : p.jenis_kelamin === 'PEREMPUAN' ? 'P' : '';
    if (!isWantedOrSuspect) {
      lines.push({ text: (p.nama_lengkap || '').slice(0, 28).toUpperCase(), bold: true, size: 12 });
      lines.push({ text: `NIK: ${maskNik(p.nik)}`, size: 9 });
      lines.push({ text: `${gShort} · ${umur != null ? umur+'th' : '—'} · ${(p.kota_kabupaten || '').slice(0,18)}`, size: 9 });
      if (p.pekerjaan) lines.push({ text: p.pekerjaan.slice(0, 28), size: 9 });
      if (plates.length) lines.push({ text: `Plat: ${plates.slice(0, 2).join(', ')}`, size: 9 });
      lines.push({ text: `${getIntelLabel(intel || 'CLEAR', currentLang())} · ${conf}%`, size: 9, bold: true });
    } else {
      lines.push({ text: (p.nama_lengkap || '').slice(0, 28).toUpperCase(), bold: true, size: 12 });
      lines.push({ text: `⚠ ${getIntelLabel(intel, currentLang())}`, size: 11, bold: true });
      lines.push({ text: `NIK: ${maskNik(p.nik)} · ${(p.kota_kabupaten || '').slice(0,16)}`, size: 9 });
      if (plates.length) lines.push({ text: `Plat: ${plates.slice(0, 2).join(', ')}`, size: 9 });
      lines.push({ text: `ALERT DIKIRIM · ${conf}%`, size: 9 });
    }
  } else if (isKnownNoRow) {
    lines.push({ text: `DIKENAL · NIK ${maskNik(face.match_nik)}`, bold: true, size: 12 });
    lines.push({ text: 'Data penduduk tidak ditemukan', size: 9 });
    lines.push({ text: `${conf}%`, size: 9 });
  } else {
    const gShort = face.gender === 'L' ? 'L' : face.gender === 'P' ? 'P' : '';
    lines.push({ text: 'TIDAK DIKENAL', bold: true, size: 12 });
    lines.push({ text: `${gShort || '—'} · ±${face.age || '?'} th`, size: 9 });
    lines.push({ text: `ALERT · ${conf}%`, size: 9 });
  }

  const cardH = lines.length * 14 + 14; // approximate card height
  const cardX = Math.max(0, Math.min(x1, w - 240));
  const cardY = Math.min(y2 + 4, h - cardH - 2);
  drawInfoCard(ctx, cardX, cardY, lines, { bg: cardBg, fg: cardFg, accent, lineH: 14, minW: 200 });
}

// Indonesian translation for vehicle classes. Default uppercase. Render saat lang === 'id'.
// Memuat kelas khas D-FINE v2b (angkot, becak, gerobak, sepeda, roda_tiga).
const VEHICLE_LABEL_ID = {
  car: 'MOBIL',
  truck: 'TRUK',
  bus: 'BUS',
  van: 'MINIBUS',
  motorcycle: 'MOTOR',
  angkot: 'ANGKOT',
  becak: 'BECAK',
  gerobak: 'GEROBAG',
  sepeda: 'SEPEDA',
  roda_tiga: 'RODA TIGA',
  bicycle: 'SEPEDA',
};

function vehicleLabelLocalized(rawLabel, lang) {
  if (lang === 'id') {
    return VEHICLE_LABEL_ID[rawLabel?.toLowerCase()] || (rawLabel || '').toUpperCase();
  }
  return (rawLabel || '').toUpperCase();
}

export function drawVehicleBbox(ctx, obj, plateInfo, w, h) {
  const [x1, y1, x2, y2] = obj.bbox;
  const lang = currentLang();
  const labelLocal = vehicleLabelLocalized(obj.label, lang); // JENIS: Mobil/Motor/Truk/Bus
  const warnaVis = obj._warna?.name || null;                  // WARNA hasil deteksi piksel
  // Plate text dari OCR (kalau ada). Fallback ke label kendaraan ID-localized.
  const hasPlate = !!(plateInfo?.plate);
  const plate = hasPlate ? plateInfo.plate : labelLocal;
  const v = plateInfo?.info || null;
  const intel = v?.intel || null;
  const isWantedOrSuspect = intel === 'WANTED' || intel === 'SUSPECT';

  // F-001 state colors:
  //   v + CLEAR/null intel → green (identified)
  //   v + WANTED/SUSPECT   → red (alert)
  //   no v                 → red (unregistered)
  let boxColor, cardBg, cardFg, accent;
  if (v && !isWantedOrSuspect) {
    boxColor = STATE_COLOR.identified;
    cardBg = 'rgba(0,255,136,0.96)'; cardFg = '#02060e'; accent = '#00d4ff';
  } else if (v && isWantedOrSuspect) {
    boxColor = STATE_COLOR.wanted;
    cardBg = 'rgba(255,45,85,0.96)'; cardFg = '#ffffff'; accent = '#ffd60a';
  } else {
    boxColor = STATE_COLOR.unregistered;
    cardBg = 'rgba(255,45,85,0.96)'; cardFg = '#ffffff'; accent = '#ff2d55';
  }

  ctx.fillStyle = boxColor + '14';
  ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
  drawCorners(ctx, x1, y1, x2, y2, boxColor, isWantedOrSuspect ? 3 : 2.4);

  // Font dibesarkan plate 14, sub 11. Background solid (alpha 0.96) untuk
  // readability di video CCTV noisy.
  const lines = [{ text: plate, bold: true, size: 14 }];
  // Golongan Bina Marga (PUPR). Bintang (*) = estimasi (mis. sumbu truk belum
  // terhitung dari tampak depan).
  if (obj._golongan) {
    const g = obj._golongan;
    lines.push({ text: `${golonganBadge(g)} · ${g.nama}`, size: 10, bold: true });
  }
  if (v) {
    // Warna: pakai DB kalau ada, jika kosong pakai hasil deteksi vision.
    const warnaShown = v.warna || warnaVis || null;
    const merk = v.merk_model
      ? `${v.merk_model}${warnaShown ? ' · '+warnaShown : ''}`
      : (warnaShown || null);
    if (merk) lines.push({ text: merk.slice(0, 32), size: 11 });
    if (v.nama_pemilik) lines.push({ text: `Pemilik: ${v.nama_pemilik.slice(0,24)}`, size: 11 });
    if (v.status_stnk) {
      const s = String(v.status_stnk).toUpperCase();
      const color = s === 'AKTIF' ? '#003322' : s === 'EXPIRED' ? '#7a1029' : '#7a3a10';
      lines.push({ text: s === 'AKTIF' ? `STNK ${s}` : `⚠ STNK ${s}`, size: 11, bold: true, color });
    } else if (v.masa_berlaku_stnk) {
      const exp = new Date(v.masa_berlaku_stnk) < new Date();
      lines.push({ text: exp ? '⚠ STNK EXPIRED' : 'STNK OK', size: 11, bold: true, color: exp ? '#7a1029' : '#003322' });
    }
  } else {
    // Unregistered vehicle — sub-line 1: "Tidak terdaftar" / "Unregistered"
    lines.push({
      text: lang === 'id' ? 'Tidak terdaftar di sistem' : 'Not registered',
      size: 11, bold: true,
    });
    // Sub-line 2: JENIS + WARNA (dari vision) + confidence
    lines.push({
      text: `${labelLocal}${warnaVis ? ' ' + warnaVis.toUpperCase() : ''} · ${Math.round(obj.confidence * 100)}%`,
      size: 11,
    });
    // Sub-line 3: kalau OCR baca raw text tapi DB miss, tampilkan teks tsb
    if (plateInfo?.raw_text && !hasPlate) {
      lines.push({
        text: lang === 'id' ? `OCR: "${plateInfo.raw_text}" (tidak valid)` : `OCR: "${plateInfo.raw_text}" (invalid)`,
        size: 10,
      });
    } else if (!hasPlate) {
      lines.push({
        text: lang === 'id' ? 'Plat tidak terbaca' : 'Plate unreadable',
        size: 10,
      });
    }
  }
  const cardX = Math.max(0, Math.min(x1, w - 220));
  const cardY = Math.min(y2 + 4, h - 80);
  drawInfoCard(ctx, cardX, cardY, lines, {
    bg: cardBg, fg: cardFg, accent, lineH: 17, minW: 180,
  });
}

// F-001: render pre-enrichment YELLOW "Memindai..." bbox.
// Dipanggil SEGERA setelah HF /analyze return raw data, BEFORE await DB lookup.
// Mengisi gap visual antara detection & identification — user lihat AI sedang kerja.
export function drawIdentifyingBbox(ctx, bbox, kind = 'object') {
  const [x1, y1, x2, y2] = bbox;
  const color = STATE_COLOR.identifying;
  drawCorners(ctx, x1, y1, x2, y2, color, 2);

  const labelMap = { face: 'Memindai wajah...', vehicle: 'Memindai plat...', object: 'Memindai...' };
  const label = labelMap[kind] || labelMap.object;
  ctx.font = 'bold 12px var(--font-sans), "Nunito", "IBM Plex Sans", sans-serif';
  const tw = ctx.measureText(label).width + 16;
  const ly = y1 > 22 ? y1 - 20 : y2;
  ctx.fillStyle = color;
  ctx.fillRect(x1, ly, tw, 18);
  ctx.fillStyle = '#02060e';
  ctx.fillText(label, x1 + 8, ly + 14);
}

export function renderRawBboxes(canvas, sourceW, sourceH, rawData) {
  if (!canvas || !rawData) return;
  canvas.width = sourceW;
  canvas.height = sourceH;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, sourceW, sourceH);
  // Faces yellow
  for (const f of (rawData.faces || [])) {
    if (f.bbox) drawIdentifyingBbox(ctx, f.bbox, 'face');
  }
  // Vehicles yellow
  for (const o of (rawData.objects || [])) {
    if (VEHICLE_LABELS.has(o.label) && o.bbox) drawIdentifyingBbox(ctx, o.bbox, 'vehicle');
  }
  // Senjata merah (alert)
  for (const o of (rawData.objects || [])) {
    if (WEAPON_LABELS.has((o.label || '').toLowerCase()) && o.bbox) drawWeaponBbox(ctx, o);
  }
}

export function drawObjectBbox(ctx, obj /*, w, h */) {
  const [x1, y1, x2, y2] = obj.bbox;
  ctx.strokeStyle = '#ffd60a';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

  // Font dibesarkan 10→12px untuk readability, padding 6+10 sesuai spec.
  const label = `${obj.label.toUpperCase()} · ${Math.round(obj.confidence*100)}%`;
  ctx.font = 'bold 12px "IBM Plex Mono", monospace';
  const tw = ctx.measureText(label).width + 16;
  const ly = y1 > 22 ? y1 - 20 : y2;
  ctx.fillStyle = 'rgba(255,214,10,1.0)';
  ctx.fillRect(x1, ly, tw, 18);
  ctx.fillStyle = '#02060e';
  ctx.fillText(label, x1 + 8, ly + 14);
}

// Senjata = alert merah, label SENJATA + jenis.
export function drawWeaponBbox(ctx, obj) {
  const [x1, y1, x2, y2] = obj.bbox;
  ctx.fillStyle = 'rgba(255,45,85,0.16)';
  ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
  ctx.strokeStyle = '#ff2d55';
  ctx.lineWidth = 2.5;
  ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

  const label = `SENJATA · ${(obj.label || '').toUpperCase()} ${Math.round((obj.confidence || 0) * 100)}%`;
  ctx.font = 'bold 12px "IBM Plex Mono", monospace';
  const tw = ctx.measureText(label).width + 16;
  const ly = y1 > 22 ? y1 - 20 : y2;
  ctx.fillStyle = '#ff2d55';
  ctx.fillRect(x1, ly, tw, 18);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, x1 + 8, ly + 14);
}

// ── One-shot: render everything onto a canvas ────────────────────────────────

// True bila titik-tengah `inner` (bbox plat [x1,y1,x2,y2]) berada di dalam
// kotak `outer` (bbox kendaraan). Dipakai memasangkan plat ke kendaraan.
function bboxCenterInside(inner, outer) {
  if (!Array.isArray(inner) || !Array.isArray(outer) || inner.length < 4 || outer.length < 4) return false;
  const cx = (inner[0] + inner[2]) / 2;
  const cy = (inner[1] + inner[3]) / 2;
  return cx >= outer[0] && cx <= outer[2] && cy >= outer[1] && cy <= outer[3];
}

export function renderAllBboxes(canvas, sourceW, sourceH, scanResult) {
  if (!canvas) return;
  // Mengubah width/height mengalokasi ulang kanvas; dengan gambar ulang ~15x/detik (rAF) itu mahal. Ganti hanya bila beda.
  if (canvas.width !== sourceW) canvas.width = sourceW;
  if (canvas.height !== sourceH) canvas.height = sourceH;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, sourceW, sourceH);

  const { enrichedFaces, enrichedPlates, vehicleObjs, otherObjs, weaponObjs } = scanResult;
  // 1. Vehicles (orange) — pasangkan plat ke kendaraan lewat GEOMETRI, bukan
  //    kesamaan string bbox. Detektor kendaraan (YOLO) dan detektor plat/OCR
  //    menghasilkan koordinat berbeda, jadi perbandingan string bbox nyaris
  //    selalu gagal → kendaraan terdaftar salah dirender "tidak terdaftar".
  //    Cocokkan bila titik-tengah plat berada di dalam kotak kendaraan.
  const usedPlates = new Set();
  for (const obj of vehicleObjs) {
    let plateInfo =
      enrichedPlates?.find(p => !usedPlates.has(p) && bboxCenterInside(p.from_bbox, obj.bbox)) ||
      enrichedPlates?.find(p => !usedPlates.has(p) && p.from_bbox?.join(',') === obj.bbox.join(',')) ||
      null;
    // Kasus umum demo: 1 kendaraan + 1 plat tanpa from_bbox → pasangkan.
    if (!plateInfo && vehicleObjs.length === 1 && enrichedPlates?.length === 1 && !enrichedPlates[0].from_bbox) {
      plateInfo = enrichedPlates[0];
    }
    if (plateInfo) usedPlates.add(plateInfo);
    drawVehicleBbox(ctx, obj, plateInfo, sourceW, sourceH);
  }
  // 2. Other objects (yellow) — saat ini kosong (di luar scope), defensif saja
  for (const obj of otherObjs) drawObjectBbox(ctx, obj);
  // 3. Senjata (merah, alert)
  for (const obj of (weaponObjs || [])) drawWeaponBbox(ctx, obj);
  // 4. Faces (cyan / red) drawn last so they overlay vehicle cards
  for (const face of enrichedFaces) drawFaceBbox(ctx, face, sourceW, sourceH);
}

// ── Alert dispatch with dedup (per-NIK 60s, per-camera-unknown 5min, per-plate 60s) ──

const _lastAlert = {};
const TTL = { known: ALERT_DEDUP_KNOWN, unknown: ALERT_DEDUP_UNKNOWN, plate: ALERT_DEDUP_VEHICLE };

// Module-level flag — log alert insert error sekali per session.
const _aiScannerState = { alertWarned: false };

export async function dispatchAlerts(scanResult, kameraInfo /* {id,nama,lokasi} */) {
  const now = Date.now();
  const ts = new Date().toLocaleTimeString('id-ID', { hour12: false });
  const today = new Date().toLocaleDateString('id-ID');
  const lokasi = `${kameraInfo.nama}${kameraInfo.lokasi ? ' · '+kameraInfo.lokasi : ''}`;

  const inserts = [];

  for (const f of scanResult.enrichedFaces) {
    const conf = Math.round((f.match_score ?? f.confidence ?? 0) * 100);
    if (f._person) {
      const intel = f._person.status_intelijen;
      if (intel === 'WANTED' || intel === 'SUSPECT') {
        const key = `${kameraInfo.id}:known:${f._person.nik}`;
        if (now - (_lastAlert[key] || 0) >= TTL.known) {
          _lastAlert[key] = now;
          inserts.push({
            jenis: 'WAJAH_DIKENAL', subjek: f._person.nama_lengkap,
            lokasi, waktu: ts, tanggal: today, confidence: conf,
            tindakan: `${intel} terdeteksi di ${kameraInfo.nama}. Tindak lanjut segera.`,
            status_subjek: intel, nik: f._person.nik, resolved: false,
          });
        }
      }
    } else {
      const key = `${kameraInfo.id}:unknown`;
      if (now - (_lastAlert[key] || 0) >= TTL.unknown) {
        _lastAlert[key] = now;
        // Explicit nik=null + status_subjek=null supaya schema match
        // konsisten dengan WAJAH_DIKENAL row di array yang sama.
        // Mixed-schema bulk insert kadang 400 di Supabase REST.
        inserts.push({
          jenis: 'WAJAH_TIDAK_DIKENAL', subjek: 'Wajah Tidak Dikenal',
          lokasi, waktu: ts, tanggal: today,
          confidence: Math.round((f.confidence ?? 0) * 100),
          tindakan: `Wajah asing terdeteksi di ${kameraInfo.nama}.`,
          status_subjek: null, nik: null, resolved: false,
        });
      }
    }
  }

  for (const p of scanResult.enrichedPlates) {
    const intel = p.info?.intel;
    if (intel === 'WANTED' || intel === 'SUSPECT') {
      const key = `${kameraInfo.id}:plate:${p.plate}`;
      if (now - (_lastAlert[key] || 0) >= TTL.plate) {
        _lastAlert[key] = now;
        inserts.push({
          jenis: 'KENDARAAN',
          subjek: `${p.plate} (${p.info?.nama_pemilik || 'pemilik tidak diketahui'})`,
          lokasi, waktu: ts, tanggal: today, confidence: 100,
          tindakan: `Kendaraan ${intel} terdeteksi.`,
          status_subjek: intel, nik: p.info?.nik_pemilik || null,
          resolved: false,
        });
      }
    }
  }

  if (inserts.length) {
    // Insert per-row supaya 1 row dengan schema mismatch tidak gagalkan batch.
    // Mixed schemas (WAJAH_TIDAK_DIKENAL tanpa status_subjek/nik) di array tunggal
    // bikin Supabase REST 400. Per-row insert = isolasi error.
    const results = await Promise.allSettled(
      inserts.map(row => supabase.from('alerts').insert(row))
    );
    const errs = results.filter(r => r.status === 'rejected' || r.value?.error);
    if (errs.length > 0 && !_aiScannerState.alertWarned) {
      _aiScannerState.alertWarned = true;
      console.warn(`[aiScanner] alert insert ${errs.length}/${inserts.length} gagal — schema/RLS check needed (logged once per session)`);
    }
  }
  return inserts.length;
}

// =============================================================================
// HF KEEP-ALIVE PING — heartbeat tiap 4 menit supaya HF Space tidak sleep
// DAN models tetap loaded di memory.
// /health saja tidak cukup — itu hanya keep container hidup, BUKAN keep model.
// Solusi: kirim dummy 1×1px JPEG ke /analyze setiap heartbeat → trigger
// model inference → models stay warm di memory.
// =============================================================================
export let hfStatus = 'checking';
let _pingTimer = null;

// 1×1 transparent pixel as JPEG (smallest valid image untuk trigger model load)
// Pre-encoded supaya tidak perlu canvas/blob conversion setiap ping.
const _DUMMY_JPEG_DATA_URI = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////2wBDAf//////////////////////////////////////////////////////////////////////////////////////wAARCAABAAEDAREAAhEBAxEB/8QAFAABAAAAAAAAAAAAAAAAAAAACf/EABQBAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhADEAAAAB/AP//Z';

async function _dummyBlob() {
  const r = await fetch(_DUMMY_JPEG_DATA_URI);
  return r.blob();
}

// Sprint 004 B-001: AI service grace period — single ping fail jangan langsung
// flag offline. Butuh 3 consecutive fail dalam window 12 detik (3× 4s retry)
// baru transisi ke offline. Mengurangi UI flicker karena network blip / HF
// Space scaling. Reset counter saat sukses berapapun.
const RETRY_ATTEMPTS = 3;
const RETRY_INTERVAL_MS = 4000;
const HEALTH_TIMEOUT_MS = 15000;

async function _pingOnce() {
  try {
    const r = await fetch(`/api/hf-health`, { signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS) });
    if (!r.ok) return { ok: false, data: null };
    const data = await r.json().catch(() => null);
    return { ok: true, data };
  } catch {
    return { ok: false, data: null };
  }
}

// Mulai loop ping. `onStatusChange(next)` dipanggil hanya saat status berubah.
// Returns cleanup function untuk stop ping (dipakai di useEffect cleanup).
export function startHFKeepAlive(onStatusChange) {
  const ping = async () => {
    // Tier-1 fast path: 1 attempt. Jika sukses, reset & exit.
    const first = await _pingOnce();
    if (first.ok) {
      const data = first.data;
      // Backend models: yolo + face wajib loaded; ocr optional (lazy-load).
      const modelsLoaded = data?.models_loaded
        && data.models_loaded.yolo
        && data.models_loaded.face;
      if (!modelsLoaded) {
        // Warm-up via /analyze dummy (HF lazy-load model on first /analyze call)
        try {
          console.log('[HFPing] Models cold — warming up via /analyze dummy...');
          const blob = await _dummyBlob();
          const fd = new FormData();
          fd.append('file', blob, 'warmup.jpg');
          await fetch('/api/analyze', { method: 'POST', body: fd, signal: AbortSignal.timeout(60000) });
          console.log('[HFPing] Warm-up complete.');
        } catch (e) { console.warn('[HFPing] Warm-up failed:', e?.message); }
      }
      if (hfStatus !== 'online') {
        hfStatus = 'online';
        onStatusChange?.('online');
        console.log('[HFPing] Status: online | models:', modelsLoaded ? 'loaded' : 'cold');
      }
      return;
    }

    // First attempt failed — masuk retry mode 3× dalam 12 detik sebelum flag offline
    let consecutiveFails = 1;
    for (let attempt = 2; attempt <= RETRY_ATTEMPTS; attempt++) {
      await new Promise(r => setTimeout(r, RETRY_INTERVAL_MS));
      const retry = await _pingOnce();
      if (retry.ok) {
        // Recovered before flagging offline
        if (hfStatus !== 'online') {
          hfStatus = 'online';
          onStatusChange?.('online');
          console.log(`[HFPing] Recovered after ${attempt - 1} fail(s) — back online`);
        }
        return;
      }
      consecutiveFails++;
    }

    // ≥3 consecutive fails (~12s window) — confirmed offline
    if (hfStatus !== 'offline') {
      hfStatus = 'offline';
      onStatusChange?.('offline');
      console.warn(`[HFPing] ${consecutiveFails} consecutive fails — flag offline`);
    }
  };
  ping();
  // 4 menit — di bawah HF gcTimeout (5 menit) supaya models tidak di-evict
  _pingTimer = setInterval(ping, 240_000);
  return () => {
    clearInterval(_pingTimer);
    _pingTimer = null;
  };
}

// =============================================================================
// AUTO SYNC EMBEDDING 512-dim via HF /extract-embedding
// Dipanggil saat HF online — encode foto Supabase yang belum punya embedding.
// Endpoint: POST {HF_URL}/extract-embedding (multipart file=...) →
//   { detected: bool, embedding: number[] | null, face_count: int }
// =============================================================================
export async function syncMissingEmbeddings(supabaseClient, onProgress) {
  try {
    const { data: missing, error } = await supabaseClient
      .from('penduduk')
      .select('nik, nama_lengkap, foto_url')
      .is('face_embedding', null)
      .not('foto_url', 'is', null);

    if (error) {
      console.error('[EmbSync] Query error:', error);
      return { synced: 0, failed: 0 };
    }

    if (!missing?.length) {
      console.log('[EmbSync] Semua penduduk sudah punya face_embedding ✅');
      return { synced: 0, failed: 0 };
    }

    console.log(`[EmbSync] ${missing.length} penduduk perlu di-encode via HF`);
    let synced = 0, failed = 0;

    for (const p of missing) {
      try {
        onProgress?.({ current: synced + failed + 1, total: missing.length, nama: p.nama_lengkap });
        const fotoRes = await fetch(p.foto_url, { signal: AbortSignal.timeout(10000) });
        if (!fotoRes.ok) throw new Error('Foto tidak bisa didownload');
        const blob = await fotoRes.blob();
        const form = new FormData();
        form.append('file', blob, 'foto.jpg');
        const hfRes = await fetch('/api/extract-embedding', {
          method: 'POST',
          body:   form,
          signal: AbortSignal.timeout(30000),
        });
        if (!hfRes.ok) throw new Error('KES Engine encode gagal: ' + hfRes.status);
        const hfData = await hfRes.json();
        if (!hfData.detected || !Array.isArray(hfData.embedding)) {
          throw new Error('Wajah tidak terdeteksi di foto');
        }
        // L2-normalize supaya konsisten dgn writer lain (edge fn / storage.js)
        const raw = hfData.embedding;
        const nrm = Math.sqrt(raw.reduce((s, v) => s + v * v, 0)) || 1;
        const normalized = raw.map(v => v / nrm);
        // rowcount guard: jangan klaim sukses kalau update kena 0 baris
        const { data: upd, error: upErr } = await supabaseClient
          .from('penduduk')
          .update({ face_embedding: normalized })
          .eq('nik', p.nik)
          .select('nik');
        if (upErr) throw upErr;
        if (!upd || upd.length === 0) throw new Error(`0 baris ter-update (NIK ${p.nik})`);
        synced++;
        console.log(`[EmbSync] ✅ ${p.nama_lengkap} (NIK: ${p.nik})`);
      } catch (err) {
        failed++;
        console.warn(`[EmbSync] ❌ ${p.nik}: ${err.message}`);
      }
      await new Promise(r => setTimeout(r, 1000));
    }

    console.log(`[EmbSync] Selesai: ${synced} berhasil, ${failed} gagal`);
    return { synced, failed };
  } catch (err) {
    console.error('[EmbSync] Error:', err);
    return { synced: 0, failed: 0 };
  }
}
